import { format } from 'date-fns';
import { LockKeyhole } from 'lucide-react';
import { useSetRecoilState } from 'recoil';

import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Heading } from '@/components/heading';
import { Icon } from '@/components/icon';
import { Text } from '@/components/text';
import { Chart } from '@/components/ui/chart';
import { ChartConfig } from '@/components/ui/chart/base';
import { SHORT_DATE_FORMAT } from '@/lib/constants';
import { lockedDialogIsOpenState } from '@/state/user';
import { ChartDatum } from '@/types/charts';
import { tryParseDate } from '@/utils/format/date-time';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

export interface MiniChartCardProps {
  heading?: string;
  body?: string;
  chartHeading?: string;
  chartHeadingHighlight?: string;
  chartSubHeading?: string;
  chartTooltipSubHeading?: string;
  badgeText?: string;
  badgeType: 'positive' | 'negative';
  badgeArrowDirection?: 'up' | 'down';
  button?: {
    text: string;
    onClick: () => void;
  };
  isLocked?: boolean;
  data: ChartDatum[];
  id: string;
  chartConfig: ChartConfig;
  chartValueSuffix?: string;
  type?: 'area' | 'line' | 'bar';
}

const MiniChartCard = ({
  heading,
  body,
  chartHeading,
  chartHeadingHighlight,
  chartSubHeading,
  chartTooltipSubHeading,
  button,
  badgeText,
  badgeType,
  badgeArrowDirection,
  isLocked = false,
  id,
  data,
  chartConfig,
  chartValueSuffix,
  type = 'area',
}: MiniChartCardProps) => {
  const setLockedDialogIsOpen = useSetRecoilState(lockedDialogIsOpenState);

  const handleLockedDialogOpen = () => {
    setLockedDialogIsOpen(true);

    trackUser.event(`Click Locked: MiniChartCard`, { id });
  };

  const badgeArrowDirectionFinal = badgeArrowDirection ?? (badgeType === 'positive' ? 'up' : 'down');

  const {
    base,
    headingStyles,
    bodyStyles,
    main,
    chartHeadingStyles,
    chartHeadingHighlightStyles,
    chartSubHeadingStyles,
    badge,
    badgeTextStyles,
    iconStyles,
  } = styles();

  return (
    <article className={base()}>
      {Boolean(heading) && (
        <Heading variant="h3" className={headingStyles()}>
          {heading}
        </Heading>
      )}
      {Boolean(body) && (
        <Text variant="secondary" className={bodyStyles()}>
          {body}
        </Text>
      )}
      <div className={main()}>
        <div>
          {Boolean(chartHeading) && (
            <Heading variant="h3" as="h4" className={chartHeadingStyles()}>
              {chartHeading} <span className={chartHeadingHighlightStyles()}>{chartHeadingHighlight}</span>
            </Heading>
          )}
          {Boolean(chartSubHeading) && <Text className={chartSubHeadingStyles()}>{chartSubHeading}</Text>}
        </div>

        {badgeText && (
          <Badge variant={`${badgeType}Light`} className={badge()}>
            <Icon
              name={`${badgeArrowDirectionFinal === 'up' ? 'up' : 'down'}-solid`}
              size="6"
              color={badgeType === 'positive' ? 'green.500' : 'error'}
              className={iconStyles()}
            />
            <span className={badgeTextStyles()}>{badgeText}</span>
          </Badge>
        )}
      </div>

      <div className="-mx-3.5">
        <Chart
          data={data}
          chartConfig={chartConfig}
          type={type}
          settings={{
            chart: {
              margin: { top: 7, right: 6, bottom: 0, left: 4 },
            },
            tooltip: {
              tooltipSubHeading: chartTooltipSubHeading,
              valueSuffix: chartValueSuffix,
            },
            axes: {
              xAxis: {
                tickFormatter: (value: string) => {
                  const date = tryParseDate(value);
                  return date ? format(date, SHORT_DATE_FORMAT) : '';
                },
              },
            },
          }}
        />
      </div>
      {button && (
        <Button
          onClick={!isLocked ? button.onClick : () => handleLockedDialogOpen()}
          iconLeft={isLocked ? <LockKeyhole size={14} /> : null}
          size="small"
          className="mt-6"
        >
          {button.text}
        </Button>
      )}
    </article>
  );
};

const styles = tv({
  slots: {
    base: 'rounded-lg border border-grey-300 bg-white p-8 shadow-md',
    headingStyles: 'font-semibold',
    bodyStyles: 'mb-8 text-grey-600',
    main: 'mb-6 flex items-end justify-between',
    chartHeadingStyles: 'mb-0 font-semibold',
    chartSubHeadingStyles: 'mb-0 text-grey-500',
    badge: 'mb-1.5',
    iconStyles: 'mr-0.5',
    badgeTextStyles: 'mr-0.5 text-baseSm font-medium leading-none text-text-primary',
    chartHeadingHighlightStyles: 'rounded-md bg-grey-100 px-1.5 py-0.5',
  },
});

export { MiniChartCard };
